import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../../contexts/AuthContext';
import axios from 'axios';
import '../../../styles/login.css';

// Indian Flag SVG
const IndianFlag = ({ className = "flag-icon" }) => (
  <svg viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" className={className}>
    <rect fill="#FF9933" width="900" height="200"/>
    <rect fill="#FFFFFF" y="200" width="900" height="200"/>
    <rect fill="#138808" y="400" width="900" height="200"/>
    <circle cx="450" cy="300" r="60" fill="#000080"/>
    <circle cx="450" cy="300" r="52" fill="#FFFFFF"/>
    <circle cx="450" cy="300" r="16" fill="#000080"/>
  </svg>
);

// Password strength calculator
const getPasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;
  
  if (strength <= 2) return { level: 'weak', color: '#EF4444', text: 'Weak', width: '33%' };
  if (strength <= 3) return { level: 'medium', color: '#F59E0B', text: 'Medium', width: '66%' };
  return { level: 'strong', color: '#10B981', text: 'Strong', width: '100%' };
};

const Login = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ level: '', color: '#999999', text: '', width: '0%' });
  const [mounted, setMounted] = useState(false);
  const [locationPermission, setLocationPermission] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    document.body.classList.add('login-page-video');
    setTimeout(() => setMounted(true), 100);
    requestLocationPermission();
    
    return () => {
      document.body.classList.remove('login-page-video');
    };
  }, []);

  const requestLocationPermission = async () => {
    if (!navigator.geolocation) {
      setLocationPermission('skipped');
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000
        });
      });

      const { latitude, longitude } = position.coords;
      
      try {
        const response = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
        const address = response.data.address || {};
        
        setUserLocation({
          latitude,
          longitude,
          city: address.city || address.town || address.village || address.state || 'Unknown',
          country: address.country || 'Unknown',
          ip: 'browser-geo'
        });
        setLocationPermission('granted');
      } catch (geoError) {
        setUserLocation({ latitude, longitude, city: 'Unknown', country: 'Unknown', ip: 'browser-geo' });
        setLocationPermission('granted');
      }
    } catch (error) {
      setLocationPermission('skipped');
      try {
        const response = await axios.get('https://ipapi.co/json/');
        setUserLocation({
          latitude: response.data.latitude,
          longitude: response.data.longitude,
          city: response.data.city || 'Unknown',
          country: response.data.country_name || 'Unknown',
          ip: response.data.ip
        });
        setLocationPermission('granted');
      } catch (ipError) {
        setLocationPermission('skipped');
      }
    }
  };

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (password) {
      setPasswordStrength(getPasswordStrength(password));
    } else {
      setPasswordStrength({ level: '', color: '#999999', text: '', width: '0%' });
    }
  }, [password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    try {
      await login(email, password, userLocation);
    } catch (error) {
      setLoginError(error.message || error.response?.data?.detail || 'Invalid credentials');
      const form = e.target;
      form.classList.add('shake-error');
      setTimeout(() => form.classList.remove('shake-error'), 500);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    setCapsLock(e.getModifierState('CapsLock'));
  };

  return (
    <>
      <Helmet>
        <title>Go4Garage | URGAA — Secure Login</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
      </Helmet>

      {/* Full Screen Video Background */}
      <div className="login-video-bg">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="login-video"
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
          disableRemotePlayback
          onContextMenu={(e) => e.preventDefault()}
        >
          <source src="https://customer-assets.emergentagent.com/job_urgaa-dashboard-2/artifacts/4ai7tll0_video_4k_uhd.mp4" type="video/mp4" />
        </video>
        <div className="login-video-overlay"></div>
      </div>

      {/* Logo in Top-Left Corner - Clean placement */}
      <div className="fixed top-4 left-4 z-50">
        <img 
          src="/assets/go4garage-logo-main.jpg" 
          alt="Go4Garage Logo" 
          className="h-12 w-auto object-contain rounded-lg shadow-lg bg-white p-1.5"
        />
      </div>

      {/* Login Container - No Scroll */}
      <div className={`login-fullscreen ${mounted ? 'mounted' : ''}`}>
        {/* Right Side Login Card */}
        <div className="login-card-container">
          <div className="login-card-square">
            {/* URGAA Branding */}
            <div className="login-branding">
              <h1 className="login-title-dual">
                <span className="login-orange">URGAA</span>
                <span className="login-hindi-green">' ऊर्जा '</span>
              </h1>
              <p className="login-subtitle">India's First EV Infrastructure Ecosystem</p>
              <div className="login-tagline">
                <span>Charging Tomorrow with</span>
                <span className="login-green-text">Green Energy</span>
                <IndianFlag className="login-flag" />
                <span>Made In Bharat</span>
              </div>
            </div>

            {/* Welcome Text */}
            <h2 className="login-welcome">Welcome back</h2>
            <p className="login-mfa">Multi-factor authentication enabled</p>
            
            {/* Location Status */}
            <div className={`login-location ${locationPermission === 'granted' ? 'granted' : ''}`}>
              <span className="login-location-icon">
                {locationPermission === 'granted' ? '✓' : locationPermission === 'skipped' ? '○' : '⟳'}
              </span>
              <span className="login-location-text">
                {locationPermission === 'granted' 
                  ? `Location: ${userLocation?.city}, ${userLocation?.country}`
                  : locationPermission === 'skipped'
                  ? 'Location not detected'
                  : 'Detecting location...'}
              </span>
            </div>

            {/* Login Form */}
            <form onSubmit={handleLogin} className="login-form" data-testid="login-form">
              {/* Email Input with Floating Label */}
              <div className="login-input-group">
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-input"
                  id="email"
                  name="email"
                  data-testid="email-input"
                  placeholder=" "
                  required
                  autoComplete="email"
                />
                <label htmlFor="email" className="login-label">Email or Username</label>
              </div>

              {/* Password Input with Floating Label */}
              <div className="login-input-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="login-input"
                  id="password"
                  name="password"
                  data-testid="password-input"
                  placeholder=" "
                  required
                  autoComplete="current-password"
                />
                <label htmlFor="password" className="login-label">Password</label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="login-password-toggle"
                  tabIndex="-1"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
                
                {/* Password Strength Indicator */}
                {password && (
                  <div className="login-strength">
                    <div className="login-strength-bar">
                      <div 
                        className="login-strength-fill"
                        style={{ width: passwordStrength.width, backgroundColor: passwordStrength.color }}
                      ></div>
                    </div>
                    <span className="login-strength-text" style={{ color: passwordStrength.color }}>
                      {passwordStrength.text}
                    </span>
                  </div>
                )}
                {capsLock && <p className="login-caps">⚠️ Caps Lock is ON</p>}
              </div>

              {/* Error Message */}
              {loginError && <div className="login-error">{loginError}</div>}

              {/* Form Options */}
              <div className="login-options">
                <label className="login-checkbox">
                  <input type="checkbox" data-testid="remember-checkbox" />
                  <span>Remember me</span>
                </label>
                <a href="/forgot-password" className="login-link">Forgot password?</a>
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                className="login-button" 
                data-testid="login-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="login-spinner"></span>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>

              {/* Security Note */}
              <p className="login-security">🔒 End-to-end encrypted • SOC 2 compliant</p>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
